import toast from 'react-hot-toast';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';

import SessionService from '@/services/storage/session-service';
import UserProgressStorageService from '@/services/storage/user-progress-storage-service';
import UserGroupStorageService from '@/services/storage/user-group-storage-service';
import ActiveGroupCacheService from '@/services/storage/active-group-cache-service';
import ActiveLessonCacheService from '@/services/storage/active-lesson-cache-service';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import InsightsCacheService from '@/services/storage/insights-cache-service';
import ActiveStudyCacheService from '@/services/storage/active-study-cache-service';
import UserStudyStorageService from '@/services/storage/user-study-storage-service';
import Request from '@/services/util/request-service';
import ProfileService from '@/services/profile/profile-service';

import { SignUpFormData } from 'auth-types';

const AuthService = {
  isLoggedIn(): boolean {
    const authCookie = SessionService.getSessionCookie();
    return !!authCookie && authCookie.loggedIn;
  },

  async signOut(router: AppRouterInstance, callback?: () => void): Promise<void> {
    this.signOutReset();

    //call signout endpoint
    const response = await (await Request.get('auth/logout'))?.json();

    if (callback) callback();
    if (!!response && response.status) {
      router.push('/');
    }
  },

  signOutReset() {
    //delete local cookies
    SessionService.resetSessionCookie();
    UserCourseStorageService.resetCourseCookie();
    UserProgressStorageService.resetUserCourseProgressCookie();
    UserCourseStorageService.resetCourseData();
    ActiveLessonCacheService.resetLessonStateCookie();
    ActiveLessonCacheService.resetActiveQuizCookie();
    UserGroupStorageService.deleteMyGroupsCookie();
    ActiveGroupCacheService.resetCurrentGroupCookie();
    InsightsCacheService.resetInsightCookie();
    ActiveStudyCacheService.resetStudyStateCookie();
    ActiveStudyCacheService.resetStudyQuizStateCookie();
    UserStudyStorageService.resetStudyMaterialCookie();
    ProfileService.resetProfileCookie();
  },

  async handleSignUp(token: string | undefined, formData: SignUpFormData): Promise<boolean> {
    try {
      //send user data for user creation
      const signUpResponse = await Request.post('auth/createUser', {
        token: token,
        jwtData: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          date_of_birth: Date.parse(formData.dateOfBirth as string),
          interests: formData.interests,
          userRole: formData.userRole,
          learningStyle: formData.learningStyle,
        },
      });

      const signUpResData = await signUpResponse?.json();
      if (signUpResData.status !== 'error') {
        //user creation successful
        SessionService.setSessionCookie({
          loggedIn: true,
          exp: signUpResData.expiration,
        });

        //create initial user cookies
        UserProgressStorageService.setUserCourseProgressCookie({});
        UserCourseStorageService.setCourseCookie({
          active: '',
          all: { user: [], group: {}, enterprise: [] },
        });
        ProfileService.setProfileCookie({ interests: [], learning_goals: [] });
        return true;
      } else {
        //failed to create user
        throw new Error('Error in request for user creation. Invalid response.');
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  },

  async handleSuccessfulLogin(expiration: Date): Promise<void> {
    // set SESSION cookie
    const sessionData = {
      loggedIn: true,
      exp: expiration,
    };
    SessionService.setSessionCookie(sessionData);

    // set U_COURSE cookie
    await UserCourseStorageService.retrieveCourseCookie();

    // set U_PROGRESS
    await UserProgressStorageService.retrieveUserCourseProgressCookie();

    await ProfileService.getProfileData();

    toast.success('Successfully Logged In', { duration: 2000 });
    return;
  },
};

export default AuthService;
